import { expandHexColor } from '@/utils/hex-color'

/**
 * Height the app's fixed system bar has to have.
 *
 * Two jobs, and on a given client only one of them is doing any work:
 *
 * 1. On notched devices it covers the safe-area strip, so nothing renders
 *    under the notch. That is `env(safe-area-inset-top)`.
 * 2. On iOS Safari it owns the colour the browser paints into the status bar.
 *    Safari 26 stopped drawing an opaque status bar: it fills that strip with
 *    a colour sampled from a row a few pixels below the top of the layout
 *    viewport, and ignores `theme-color`. So whatever pins at the top bleeds
 *    into the strip and changes as the page scrolls -- the branding band, the
 *    rich text editor's toolbar -- which is what makes the strip look
 *    transparent. Safari reports `env(safe-area-inset-top)` as 0 there,
 *    because the viewport already starts below the strip, so the bar cannot be
 *    sized from the inset alone.
 */

// The surface colour, so the strip reads as part of the page rather than as
// Vuetify's default grey. Most routes already ask for this by name; the ones
// that do not used to get away with it because the bar only rendered on
// notched devices. Routes can still override it via `meta.systemBar.color`.
export const SYSTEM_BAR_DEFAULT_COLOR = 'neutral0'

// Measured against Simulator Safari, iPhone 17 Pro on iOS 26.5, driving the real
// app: a 12px band is the shallowest the sampler reads and 10px falls through to
// the document background, which is why a tinted note used to sit under a white
// strip. A bare test page reads a 6px band, so the threshold has to be measured
// in the app, not in isolation. Rounded up for margin -- the sample row is
// undocumented and can move. See .scratch/safari-statusbar-spa.
export const SAMPLED_STRIP_DEPTH = 16

// `-webkit-touch-callout` is implemented by iOS-family WebKit only, so it is
// true in every iOS browser and false everywhere else -- measured true in
// Simulator Safari 26.5 and false in macOS Safari 26.5.2. A feature query
// rather than a user agent, which iPadOS reports as "MacIntel".
const IOS_ONLY_PROPERTY = '-webkit-touch-callout'

/**
 * Whether this client's browser paints chrome over the top of the page and
 * samples our colour for it. Only iOS does. Native builds draw their own
 * status bar and get a real safe-area inset instead, and the extension popup
 * has no strip at all.
 *
 * @param {{ isNative: boolean, isExtension: boolean }} client
 * @returns {boolean}
 */
export const ownsSampledStatusStrip = ({ isNative, isExtension }) =>
  !isNative &&
  !isExtension &&
  typeof CSS !== 'undefined' &&
  CSS.supports?.(IOS_ONLY_PROPERTY, 'none') === true

/**
 * @param {number} safeAreaTop `env(safe-area-inset-top)` in px, 0 where the
 *   client has no notch to clear (every browser tab, including iOS Safari)
 * @param {boolean} ownsStrip see {@link ownsSampledStatusStrip}
 * @returns {number} height in px, 0 when there is nothing to cover
 */
export const resolveSystemBarHeight = (safeAreaTop, ownsStrip = false) => {
  const inset = safeAreaTop || 0

  return ownsStrip ? Math.max(inset, SAMPLED_STRIP_DEPTH) : inset
}

/**
 * Colour the system bar has to paint.
 *
 * Routes name a colour in `meta.systemBar.color`, which is a fine default but is
 * static -- a note detail page only learns its colour once the note is loaded.
 * So a page may override it for as long as it is mounted, via
 * {@link module:composables/useSystemBarColor}.
 *
 * @param {string|null} override colour a mounted page is asking for, if any
 * @param {string|null} routeColor `meta.systemBar.color` for the current route
 * @returns {string} a theme colour name or a CSS colour, never empty
 */
export const resolveSystemBarColor = (override, routeColor) =>
  override || routeColor || SYSTEM_BAR_DEFAULT_COLOR

/**
 * The system bar colour as a real hex.
 *
 * The bar itself takes a Vuetify theme token ("neutral0") or a CSS colour, but
 * Android Chrome tints its own toolbar from `<meta name="theme-color">` -- there
 * is no app-drawn strip on that client for the bar to paint -- and that tag only
 * understands a real colour.
 *
 * @param {string|null} color whatever resolveSystemBarColor returned
 * @param {Object<string,string>} themeColors the active Vuetify theme's palette
 * @returns {string|null} "#rrggbb", or null when it cannot be resolved
 */
export const resolveSystemBarHex = (color, themeColors) => {
  if (typeof color !== 'string' || !color) return null

  return expandHexColor(color.startsWith('#') ? color : themeColors?.[color] ?? '')
}
